import z from 'zod'
import { nullSafeBoolean, nullSafeNumber } from '../shared/schema'

// All configuration for the inbound dead-air detector lives on one document,
// settings/dead_air_detector, so a live operator can turn the feature on and
// tune it in one place without a deploy. Field names are unprefixed because the
// document already namespaces them.
export const schema = z.object({
  // Detection, snapshot capture, and reporting (Sentry + GA). No action, no UI.
  detectionEnabled: nullSafeBoolean(false),
  // The cheap per-peer repair the controller runs before any dialog.
  autoHealEnabled: nullSafeBoolean(false),
  // The reconnect dialog.
  dialogEnabled: nullSafeBoolean(false),
  // Confirmation window before the dialog shows (two persisting episodes at
  // least this far apart). 0 disables the nudge.
  nudgeWaitDurationMS: nullSafeNumber(0),

  // How long a user's dismissal of the dialog is honoured.
  //
  // Closing the dialog says nothing to the detector, so without this the run
  // clock — reset when the dialog opened — reaches nudgeWaitDurationMS again a
  // couple of episodes later and a fresh dialog opens. A client that stays deaf
  // therefore gets nudged on that cadence for as long as the fault lasts, which
  // is what the close button reads as a request to stop. This is the quiet
  // period bought by one dismissal.
  //
  // Spent only on a dismissal, and cleared by a genuine recovery: a new
  // incident is a new thing to say, so it nudges immediately rather than
  // serving out the previous incident's silence.
  //
  // 0 restores the pre-backoff behaviour, where a dismissal delays nothing.
  nudgeRedisplayDelayMS: nullSafeNumber(120000),

  // How often one listener's confirmed episodes are allowed out of the sink.
  //
  // This is the floor under nudgeWaitDurationMS, and the reason a small wait
  // buys nothing. The dialog needs TWO episodes in one run, so the earliest it
  // can appear is one interval after the first one — a wait of 5000 against
  // the default 30000 still shows the dialog at ~30s, not at 5s. Tune this to
  // move that floor; tune nudgeWaitDurationMS to sit above it.
  //
  // One number gates three keys, and lowering it costs on all three: the
  // ladder and dialog, the meeting_dead_air_scan GA row, and the per-peer
  // Sentry event (which additionally backs off, doubling from this value). It
  // is the Sentry error budget as much as a cadence, so a low value belongs in
  // a preview or a QA project rather than in production.
  //
  // Floored at 1s rather than honoured literally. The UI decides which
  // episodes belong to the same run by measuring gaps against twice this value
  // (MeetingPage), so 0 would leave a 0ms continuity window: every episode
  // would start a fresh run and the dialog would never be reached, from a
  // setting that reads like "no throttling".
  episodeMinIntervalMS: nullSafeNumber(30000).transform((val) =>
    Math.max(val, 1000)
  ),

  // Detection thresholds. These already have safe baked defaults; a set value
  // overrides so detection can be calibrated live without a deploy.
  // A non-paused element whose currentTime hasn't advanced this long is a
  // frozen playhead (lost_play).
  frozenPlayheadMS: nullSafeNumber(4000),
  // How long after subscription a track may have no attached element before
  // not_attached fires.
  attachGraceMS: nullSafeNumber(2000),
  // Continuous active-speaker time a peer must sustain before a flow check.
  sustainedSpeakerMS: nullSafeNumber(2500),
  // Largest gap between activeSpeakersChanged events still counted as one
  // continuous speaking turn. Both directions cost something, so this is the
  // threshold most likely to need calibrating: too low and an SFU that pauses
  // between events resets the turn every time, so the sustained window never
  // accumulates and flow faults go unevaluated for that turn; too high and a
  // speaker who pauses between sentences is never treated as having finished,
  // so they are judged once per meeting against a stale baseline.
  speakerFlickerGapMS: nullSafeNumber(800),
  // Cadence of the backstop structural sweep.
  backstopIntervalMS: nullSafeNumber(30000),
  // Settle window before verifying an auto-heal attempt. Spent twice in the
  // worst case: once capping the wait for the fix promises to land, then again
  // settling before the verify runs. A fix that never settles therefore delays
  // the dialog by ~2x this value.
  autoHealSettleMS: nullSafeNumber(1500),
  // How long the dialog's reconnect waits for re-subscription before probing.
  // Longer than the auto-heal window because a full room reconnect has to tear
  // down and rebuild every subscription, not nudge one element.
  nudgeSettleMS: nullSafeNumber(5000),
  // Energy delta above which the decoder is judged to have produced sound
  // (silent_decode sensitivity).
  energyEpsilon: nullSafeNumber(1e-6),
  // Fraction of concealed samples above which flat energy is attributed to
  // packet loss, not dead air.
  concealFraction: nullSafeNumber(0.1),
  // Context-ring time window. Extending this without raising ringMaxEntries
  // truncates the ring during churn.
  ringWindowMS: nullSafeNumber(30000),
  // Context-ring entry cap; raise it alongside ringWindowMS.
  ringMaxEntries: nullSafeNumber(150),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsDeadAirDetector = z.infer<typeof schema>
